import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { getApiUrl } from '../../config/api';
import './EmailActivity.css';

const EmailActivity = () => {
  const { user } = useAuth();
  const [emailData, setEmailData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBrands, setTotalBrands] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [sortBy, setSortBy] = useState('email_count');
  const [sortOrder, setSortOrder] = useState('desc');

  const brandsPerPage = 100;

  useEffect(() => {
    if (user) {
      fetchEmailActivity();
    }
  }, [user, currentPage, dateRange, sortBy, sortOrder]);

  const fetchEmailActivity = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!user) {
        setError('User not authenticated');
        return;
      }

      const token = await user.getIdToken();
      const response = await fetch(getApiUrl('/api/admin/analytics/email-activity'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: dateRange.start,
          end_date: dateRange.end,
          page: currentPage,
          limit: brandsPerPage,
          sort_by: sortBy,
          sort_order: sortOrder
        })
      });

      if (response.ok) {
        const data = await response.json();
        setEmailData(data.brands || []);
        setTotalPages(data.total_pages || 1);
        setTotalBrands(data.total_brands || 0);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to fetch email activity data');
      }
    } catch (err) {
      console.error('Error fetching email activity:', err);
      setError('Error fetching email activity data');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1); // Reset to first page when date range changes
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'active': { class: 'status-active', text: 'Active' },
      'inactive': { class: 'status-inactive', text: 'Inactive' },
      'new': { class: 'status-new', text: 'New' }
    };
    const statusInfo = statusMap[status] || { class: 'status-unknown', text: status };
    return <span className={`status-badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  if (loading && emailData.length === 0) {
    return (
      <div className="email-activity">
        <div className="loading-container">
          <p>Loading email activity data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="email-activity">
      <div className="email-activity-header">
        <h2>Email Activity by Brand</h2>
        <p>Email activity data for the last week, grouped by brand</p>
      </div>

      <div className="email-activity-controls">
        <div className="date-range-controls">
          <div className="date-input-group">
            <label htmlFor="start-date">Start Date:</label>
            <input
              type="date"
              id="start-date"
              value={dateRange.start}
              onChange={(e) => handleDateRangeChange('start', e.target.value)}
              className="date-input"
            />
          </div>
          <div className="date-input-group">
            <label htmlFor="end-date">End Date:</label>
            <input
              type="date"
              id="end-date"
              value={dateRange.end}
              onChange={(e) => handleDateRangeChange('end', e.target.value)}
              className="date-input"
            />
          </div>
          <button 
            onClick={fetchEmailActivity}
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>

        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Total Brands:</span>
            <span className="stat-value">{totalBrands.toLocaleString()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Page:</span>
            <span className="stat-value">{currentPage} of {totalPages}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      <div className="email-activity-table-container">
        <table className="email-activity-table">
          <thead>
            <tr>
              <th 
                className="sortable"
                onClick={() => handleSort('brand')}
              >
                Brand {getSortIcon('brand')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('email_count')}
              >
                Email Count {getSortIcon('email_count')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('campaign_count')}
              >
                Campaigns {getSortIcon('campaign_count')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('avg_quality_score')}
              >
                Avg Quality {getSortIcon('avg_quality_score')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('last_email_date')}
              >
                Last Email {getSortIcon('last_email_date')}
              </th>
              <th 
                className="sortable"
                onClick={() => handleSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {emailData.map((brand, index) => (
              <tr key={brand.brand || index}>
                <td className="brand-cell">
                  <div className="brand-info">
                    <span className="brand-name">{brand.brand || 'Unknown'}</span>
                    {brand.brand_category && (
                      <span className="brand-category">{brand.brand_category}</span>
                    )}
                  </div>
                </td>
                <td className="count-cell">
                  <span className="email-count">{brand.email_count?.toLocaleString() || 0}</span>
                </td>
                <td className="campaign-cell">
                  <span className="campaign-count">{brand.campaign_count || 0}</span>
                </td>
                <td className="quality-cell">
                  <div className="quality-score">
                    <span className="score-value">
                      {brand.avg_quality_score ? brand.avg_quality_score.toFixed(1) : 'N/A'}
                    </span>
                    {brand.avg_quality_score && (
                      <div className="quality-bar">
                        <div 
                          className="quality-fill"
                          style={{ width: `${(brand.avg_quality_score / 10) * 100}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                </td>
                <td className="date-cell">
                  {brand.last_email_date ? formatDate(brand.last_email_date) : 'Never'}
                </td>
                <td className="status-cell">
                  {getStatusBadge(brand.status || 'unknown')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {emailData.length === 0 && !loading && (
          <div className="no-data">
            <p>No email activity data found for the selected date range.</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="page-btn prev-btn"
          >
            ← Previous
          </button>
          
          <div className="page-info">
            <span>Page {currentPage} of {totalPages}</span>
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="page-btn next-btn"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailActivity;
